var SUPPORTED_LOCALES = require('../constants/supported-locales')
var COUNTRY_CODES = require('../constants/country-codes-by-locale')
var CURRENCY = require('../constants/currency')
var TIME_FORMATS = require('../constants/time-formats')
var DEFAULT_LOCALE = 'en-US'
var DEFAULT_CURRENCY = CURRENCY.CODES_BY_LOCALE[DEFAULT_LOCALE]
var isObject = require('../utils/is-object')

var defaultState = {
  countryCode: COUNTRY_CODES[DEFAULT_LOCALE],
  currency: DEFAULT_CURRENCY,
  currencySymbol: CURRENCY.SYMBOLS_BY_CODE[DEFAULT_CURRENCY],
  locale: DEFAULT_LOCALE,
  timezone: { abbreviation: '' },
  is12Hr: TIME_FORMATS[DEFAULT_LOCALE] === 12,
  is24Hr: TIME_FORMATS[DEFAULT_LOCALE] === 24,
}

var localeStore = {
  /**
   * Retrieve the value for a specific state key.
   * @param {string} key State key to retrieve.
   * @returns {string|Object|undefined} The value of the state key, or undefined for unsupported keys.
   */
  get: function(key) {
    // for objects, always send a copy, so we don't have direct references to the state object
    // which can allow unwanted modifications of state and garbage collection issues
    if (isObject(this.state[key])) {
      return Object.assign({}, this.state[key])
    } else {
      return this.state[key]
    }
  },
  getState: function() {
    return Object.assign({}, this.state)
  },
  /**
   * Updates the currency and currencySymbol values of the store.
   * Ignores unsupported codes.
   * @param {string} code The currency code string to set.
   * @param {boolean} skipPublish Whether to skip the publish step. For internal use only.
   * @returns {string|undefined} The formatted currency code if supported, otherwise undefined.
   */
  setCurrency: function(code, skipPublish) {
    // only set currency if it's a supported code
    if (CURRENCY.SYMBOLS_BY_CODE.hasOwnProperty(code)) {
      var formattedCode = code.toUpperCase()
      this.state.currency = formattedCode
      this.state.currencySymbol = CURRENCY.SYMBOLS_BY_CODE[formattedCode]
      !skipPublish && this.publish(['currency'])
      return formattedCode
    }
  },
  /**
   * Updates the locale, currency, currencySymbol and countryCode values in the store.
   * Ignores unsupported locales.
   * @param {string} value The locale string.
   * @returns {string|undefined} The formatted locale if supported, otherwise undefined.
   */
  setLocale: function(value) {
    var formattedLocale = value.replace(/_/g, '-')
    // only set for supported locales
    if (SUPPORTED_LOCALES.indexOf(formattedLocale) > -1) {
      var htmlElement = document.getElementsByTagName('html')[0]
      this.state.locale = formattedLocale
      this.state.countryCode = COUNTRY_CODES[formattedLocale]
      // update time format for locale
      this['set' + TIME_FORMATS[formattedLocale] + 'Hr']()
      // update html lang attribute for a11y and i18n needs
      htmlElement.setAttribute('lang', formattedLocale)
      this.publish(['locale'])
      return formattedLocale
    }
  },
  /**
   * Updates the time format to 12hr format in the store
   * @returns {undefined}
   */
  set12Hr: function() {
    this.state.is12Hr = true
    this.state.is24Hr = false
    this.publish(['time-format'])
  },
  /**
   * Updates the time format to 124hr format in the store
   * @returns {undefined}
   */
  set24Hr: function() {
    this.state.is24Hr = true
    this.state.is12Hr = false
    this.publish(['time-format'])
  },
  /**
   * Updates the timezone value in the store by merging a provided object with the existing store value.
   * @param {Object} obj An object to merge with the store's timezone object.
   * @returns {Object|undefined} The updated timezone object, or undefined if a non-object argument provided.
   */
  setTimezone: function(obj) {
    if (isObject(obj)) {
      this.state.timezone = Object.assign({}, this.state.timezone, obj)
      this.publish(['timezone'])
      return Object.assign({}, this.state.timezone)
    }
  },
  /**
   * Removes a watcher callback from a specific state key.
   * @param {array} token The token array returned from `watch`
   */
  unwatch: function(token) {
    var key = token[0]
    if (this.watchers[key]) {
      this.watchers[key] = this.watchers[key].filter(function(cb) {
        return cb != token[1]
      })
    }
  },
  /**
   * Watch a specific state key for changes and provide a callback to run when that key is changed.
   * The only state keys that will trigger watch changes are keys that have setters: currency, locale, timezone.
   * @param {string} key A state key to watch
   * @callback fn The function to execute when the specified state key is updated.
   * @return {Object} The current localeStore state
   */
  watch: function(key, fn) {
    if (!key || typeof key !== 'string' || typeof fn !== 'function') return
    if (!this.watchers[key]) this.watchers[key] = []
    this.watchers[key].push(fn)
    return [key, fn]
  },
  /**
   * Run watchers associated with a set of changed state keys.
   * @param {string[]} keys Array of key names that were updated. All related callbacks will be executed and passed the latest state.
   */
  publish: function(keys) {
    var watchers = this.watchers
    var state = this.getState()
    var fns = [].concat.apply(
      [],
      keys.map(function(key) {
        return watchers[key] || []
      })
    )
    fns.forEach(function(fn) {
      fn(state)
    })
  },
}

/**
 * Creates a localeStore for managing locale state.
 * @param {Object} [initialState] Initial state keys to set. Only keys matching available setters will be used.
 * @returns {Object} A localeStore object.
 */
function createLocaleStore(initialState) {
  var store = Object.create(localeStore)
  // initialize state & watchers
  store.state = Object.assign({}, defaultState)
  store.watchers = {}

  if (isObject(initialState)) {
    // add any initial state if we have a setter for it
    for (var key in initialState) {
      var method = 'set' + key.substr(0, 1).toUpperCase() + key.substr(1)

      if (typeof store[method] === 'function') {
        store[method](initialState[key])
      }
    }
  }
  return store
}

module.exports = createLocaleStore
